import React, { useCallback, useMemo, useRef, useState } from 'react';
// EAA-1817: Popover imported directly from antd because it is not yet
// re-exported from the UI kit barrel (src/components/ui/index.ts).
import { Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { Chip, Input, Listbox, useListboxKeyboardNav, type ListboxItem } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';
import styles from './AddRoleCombobox.module.css';

export interface AddRoleComboboxProps {
    appName: string;
    /** Options come from `GET /admin/app-roles?application=` — only what the
     * app currently declares, so a link can never point at a role that does
     * not exist (frame "56 Add role combobox"). */
    options: AppRoleOption[];
    loading: boolean;
    /** app_role keys already linked for THIS role row — rendered checked+disabled. */
    linkedAppRoles: string[];
    onSelect: (appRole: string) => void;
    disabled?: boolean;
    triggerLabel?: string;
}

/**
 * EAA-1817: the "+ vai trò" combobox — frame "56 Add role combobox". No free
 * text: the search box only filters the app's own synced catalog. Esc closes
 * without writing; Enter/Space on the trigger opens; arrow keys move through
 * the list.
 */
export function AddRoleCombobox({
    appName,
    options,
    loading,
    linkedAppRoles,
    onSelect,
    disabled,
    triggerLabel,
}: AddRoleComboboxProps) {
    const { t } = useTranslation();
    // EAA-1845: default moved from the destructure default into the body so
    // it can go through `t()` — a hook cannot be called in a parameter
    // initializer.
    const resolvedTriggerLabel =
        triggerLabel ?? t('console.roleMap.addRoleCombobox.defaultTriggerLabel');
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [requestedActiveId, setRequestedActiveId] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const linkedSet = useMemo(() => new Set(linkedAppRoles), [linkedAppRoles]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter(
            (o) => o.key.toLowerCase().includes(q) || o.name.toLowerCase().includes(q)
        );
    }, [options, query]);

    /**
     * EAA-1839: the options are the kit's `Listbox` in CONTROLLED mode — focus
     * stays in the search box above, the list only follows the highlight. An
     * already-linked role is `disabled` rather than merely "checked", which is
     * what stops Enter from re-adding it; the checked chrome comes from the
     * option class below.
     */
    const items = useMemo<ListboxItem<AppRoleOption>[]>(
        () =>
            filtered.map((option) => ({
                id: option.key,
                data: option,
                disabled: linkedSet.has(option.key),
            })),
        [filtered, linkedSet]
    );

    /**
     * EAA-1839: derived rather than stored, replacing the old `activeIndex`
     * state. The previous code had to reset the index to 0 from three different
     * places (open, every keystroke, `reset()`) to stop it pointing past a
     * shortened list; deriving it means a filter that drops the highlighted row
     * falls back to the first one with no reset to remember.
     */
    const activeId =
        requestedActiveId !== null && items.some((item) => item.id === requestedActiveId)
            ? requestedActiveId
            : (items[0]?.id ?? null);

    function reset() {
        setQuery('');
        setRequestedActiveId(null);
    }

    function handleOpenChange(next: boolean) {
        setOpen(next);
        if (next) {
            reset();
            window.setTimeout(() => inputRef.current?.focus(), 0);
        }
    }

    const commit = useCallback(
        (item: ListboxItem<AppRoleOption>) => {
            if (item.disabled) return;
            onSelect(item.data.key);
            setOpen(false);
        },
        [onSelect]
    );

    /**
     * EAA-1839: the ArrowUp/Down/Home/End/Enter stepping is now the kit's, wired
     * to the SEARCH INPUT's keydown while `Listbox` renders in controlled mode —
     * exactly the split `useListboxKeyboardNav` was exported for, so the highlight
     * moves without focus ever leaving the field the user is typing in. The
     * hand-rolled version it replaces clamped at both ends the same way but had no
     * Home/End.
     */
    const handleListKeyDown = useListboxKeyboardNav({
        items,
        activeId,
        onActiveIdChange: setRequestedActiveId,
        onSelect: commit,
    });

    function onKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Escape') {
            // EAA-1817: Esc closes without writing. Handled before the kit's
            // stepping sees the event — closing the popover is this component's
            // concern, not the list's.
            event.preventDefault();
            setOpen(false);
            return;
        }
        handleListKeyDown(event);
    }

    const content = (
        // EAA-1846: `data-testid` is the e2e hook now that the class names are
        // hashed by CSS Modules — `e2e/pages/RoleMapConsole.ts` scopes its option
        // lookup to this popover through it.
        <div className={styles.combobox} data-testid="role-map-combobox" onKeyDown={onKeyDown}>
            <label className={styles.search}>
                <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    aria-hidden="true"
                >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                </svg>
                {/* EAA-1817: kit `Input` — its ref is typed `HTMLInputElement` (same
                    shape this component's `useRef<HTMLInputElement>` already expects),
                    so the focus-on-open call below is unchanged. */}
                <Input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={styles.searchInput}
                    placeholder={t('console.roleMap.addRoleCombobox.searchPlaceholder')}
                    aria-label={t('console.roleMap.addRoleCombobox.searchAriaLabel')}
                />
            </label>
            <div className={styles.groupLabel}>
                {loading
                    ? t('console.roleMap.addRoleCombobox.loadingCatalog')
                    : t('console.roleMap.addRoleCombobox.catalogCount', {
                          count: options.length,
                          appName,
                      })}
            </div>
            {/* EAA-1839: the hand-rolled `role="listbox"` / `role="option"` pair is
                now the kit's `Listbox`, in CONTROLLED mode: `activeId` is driven by
                the search input's keydown above, so DOM focus never leaves the field.
                `optionsUnstyled` keeps the row chrome in
                `AddRoleCombobox.module.css`'s `.option` — its `--tint-menu-active` hover/active
                fill and the faux-checkbox square are CSS the kit's inline option
                palette would have overridden.

                The `optionActive` class now tracks the ARROW highlight only; the old row
                also moved it on `onMouseEnter`. The CSS gives `:hover` and `.optionActive`
                the identical fill, so nothing looks different — but Enter now commits
                the arrowed row rather than whichever row the pointer happens to rest
                on, which is what a combobox is supposed to do. */}
            <Listbox<AppRoleOption>
                className={styles.list}
                aria-label={t('console.roleMap.addRoleCombobox.listAriaLabel')}
                items={items}
                activeId={activeId}
                onActiveIdChange={setRequestedActiveId}
                onSelect={commit}
                optionsUnstyled
                optionClassName={(_item, state) =>
                    styles.option +
                    (state.disabled ? ` ${styles.optionChecked}` : '') +
                    (state.active ? ` ${styles.optionActive}` : '')
                }
                emptyState={
                    /* An empty fragment, not `null`: the kit falls back to its own
                       "no data" copy on a nullish `emptyState`, and while the
                       catalog is still loading the group label above already says
                       so — the list itself stayed blank before and still does. */
                    loading ? (
                        <></>
                    ) : (
                        <div className={styles.empty}>
                            {t('console.roleMap.addRoleCombobox.emptyResult')}
                        </div>
                    )
                }
                renderItem={(item, state) => (
                    <>
                        <span
                            className={
                                styles.checkbox +
                                (state.disabled ? ` ${styles.checkboxChecked}` : '')
                            }
                        />
                        <code className={styles.optionKey}>{item.data.key}</code>
                        <span className={styles.optionName}>
                            {state.disabled
                                ? t('console.roleMap.addRoleCombobox.checkedLabel')
                                : item.data.name || '—'}
                        </span>
                    </>
                )}
            />
            <div className={styles.divider} />
            <p className={styles.hint}>{t('console.roleMap.addRoleCombobox.hint', { appName })}</p>
        </div>
    );

    if (disabled) {
        return <Chip tone="add">{resolvedTriggerLabel}</Chip>;
    }

    return (
        // EAA-1817: keep antd's built-in `trigger="click"` (mouse click open/
        // close/outside-close all come free that way) but give `Chip` itself
        // no `onClick` — wiring one there too would double-toggle on a real
        // click, since rc-trigger already attaches its own listener to this
        // child regardless of whether it has its own handler. The outer span
        // is what adds Enter/Space (Chip's own keyboard handling only fires
        // for elements that already have `onClick`, and this one intentionally
        // doesn't).
        <Popover
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            content={content}
            placement="bottomLeft"
            overlayInnerStyle={{ padding: 0 }}
        >
            <span
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                style={{ display: 'inline-flex' }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenChange(true);
                    }
                }}
            >
                <Chip tone="add">{resolvedTriggerLabel}</Chip>
            </span>
        </Popover>
    );
}
